import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api, apiError, eur } from "@/lib/apiClient";
import { Brand } from "@/components/Brand";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ComplianceNotice } from "@/components/ComplianceNotice";
import { PrivacyConsent, LegalFooterLinks } from "@/components/LegalConsent";
import {
  Heart, ShieldCheck, ArrowRight, CheckCircle2, Loader2, Landmark, Accessibility,
  Globe, HandHeart, Info, Download, Copy, MessageCircle, CircleDot, Bike, Sparkles, AlertCircle,
  Eye, Ear,
} from "lucide-react";

const ICONS = { FISCO: Landmark, H24: Globe, SELF: Sparkles, FAMIGLIA: HandHeart, MOBILITA: Bike, AUSILI: Accessibility };
const SENSO_ICONS = { vista: Eye, udito: Ear, parola: MessageCircle };
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const VOLANTINO_URL = `${BACKEND_URL}/api/public/protezione-famiglia/volantino`;
const PUBLIC_URL = typeof window !== "undefined" ? `${window.location.origin}/protezione-famiglia` : "/protezione-famiglia";
const SHARE_TEXT = "Scopri Protezione Famiglia: la polizza infortuni H24, inclusiva e con vantaggi fiscali dedicati. Richiedi la tua proposta gratuita:";
const WHATSAPP_URL = `https://wa.me/?text=${encodeURIComponent(`${SHARE_TEXT} ${PUBLIC_URL}`)}`;

export default function ProtezioneFamiglia() {
  const formRef = useRef(null);
  const ref = typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("ref") || "" : "";
  const [cfg, setCfg] = useState(null);
  const [pacchetto, setPacchetto] = useState(() => new URLSearchParams(window.location.search).get("pacchetto") || "maxi");
  const [nucleo, setNucleo] = useState(() => new URLSearchParams(window.location.search).get("nucleo") || "nucleo_intero");
  const [form, setForm] = useState({ referente: "", email: "", telefono: "", ente: "", disabilita_grave: false, note: "" });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [privacyOk, setPrivacyOk] = useState(false);

  useEffect(() => { api.get("/public/protezione-famiglia/config").then(({ data }) => setCfg(data)).catch(() => {}); }, []);

  const pkg = useMemo(() => cfg?.packages.find((p) => p.code === pacchetto), [cfg, pacchetto]);
  const premio = pkg ? pkg.premi[nucleo] : null;
  const gLabel = (code) => cfg?.garanzie.find((g) => g.code === code)?.label || code;

  const scrollToForm = (pkgCode) => {
    if (pkgCode) setPacchetto(pkgCode);
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(PUBLIC_URL); toast.success("Link copiato — incollalo su WhatsApp o dove vuoi"); }
    catch { toast.error("Copia non riuscita, seleziona e copia manualmente il link"); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.referente.trim()) { setError("Inserisci il tuo nome."); return; }
    if (!form.email.trim()) { setError("Inserisci l'email: ti invieremo la documentazione precontrattuale (Set Informativo, DIP, DIP Aggiuntivo e MUP)."); return; }
    if (!privacyOk) { setError("Per procedere devi accettare l'informativa privacy."); return; }
    setLoading(true);
    try {
      await api.post("/public/protezione-famiglia/request", { ...form, pacchetto, nucleo, codice_collab: ref });
      setSent(true);
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    } catch (err) { setError(apiError(err)); } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-bone">
      <header className="sticky top-0 z-30 border-b border-border bg-bone/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-3 md:px-6">
          <Brand />
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={copyLink} data-testid="pf-copy-link" className="hidden gap-1.5 border-forest/30 text-forest hover:bg-sage sm:inline-flex">
              <Copy className="h-4 w-4" /> Copia link
            </Button>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="pf-whatsapp-share"
              className="hidden items-center gap-1.5 rounded-md border border-forest/30 px-3 py-1.5 text-sm font-medium text-forest transition-colors hover:bg-sage sm:inline-flex">
              <MessageCircle className="h-4 w-4" /> WhatsApp
            </a>
            <Button size="sm" onClick={() => scrollToForm()} data-testid="pf-header-cta" className="gap-1.5 bg-forest text-white hover:bg-forest/90">
              <Heart className="h-4 w-4" /> Richiedi proposta
            </Button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-4xl space-y-8 px-4 py-6 md:px-6 md:py-10" data-testid="pf-public">
        {/* HERO */}
        <div className="overflow-hidden rounded-2xl border border-forest/15 bg-forest p-8 text-white md:p-12">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white/90">
            <ShieldCheck className="h-3.5 w-3.5" /> Protezione H24 · Eticapro Infortuni Assimoco
          </div>
          <h1 className="max-w-3xl font-heading text-3xl font-semibold leading-tight md:text-5xl">
            La protezione non ha <span className="text-terracotta">barriere</span>
          </h1>
          <p className="mt-4 max-w-2xl text-sm text-white/85 md:text-lg">
            Una polizza infortuni <b>H24 e inclusiva</b>, pensata per le famiglie e per le persone con disabilità:
            copertura in tutto il mondo, tutela delle preesistenze e vantaggi fiscali dedicati. Perché serenità e
            futuro non siano un privilegio.
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-4">
            <Button size="lg" onClick={() => scrollToForm()} data-testid="pf-hero-cta" className="gap-2 bg-white text-forest hover:bg-white/90">
              Calcola la tua protezione <ArrowRight className="h-4 w-4" />
            </Button>
            <a href={VOLANTINO_URL} target="_blank" rel="noopener noreferrer" data-testid="pf-volantino-hero"
              className="inline-flex items-center gap-2 text-sm font-medium text-white/85 underline-offset-4 hover:text-white hover:underline">
              <Download className="h-4 w-4" /> Scarica il volantino (PDF)
            </a>
          </div>
          <div className="mt-5 flex flex-wrap items-center gap-2 sm:hidden">
            <Button size="sm" variant="outline" onClick={copyLink} data-testid="pf-copy-link-mobile" className="gap-1.5 border-white/30 bg-transparent text-white hover:bg-white/10">
              <Copy className="h-4 w-4" /> Copia link
            </Button>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="pf-whatsapp-share-mobile"
              className="inline-flex items-center gap-1.5 rounded-md border border-white/30 px-3 py-1.5 text-sm font-medium text-white hover:bg-white/10">
              <MessageCircle className="h-4 w-4" /> WhatsApp
            </a>
          </div>
        </div>

        {/* INCLUSIVITÀ */}
        {cfg?.inclusivita && (
          <Card data-testid="pf-inclusivita" className="rounded-2xl border-2 border-forest/15 bg-sage/40 p-6 md:p-8">
            <div className="flex items-start gap-4">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-forest text-white">
                <Accessibility className="h-5 w-5" strokeWidth={1.75} />
              </div>
              <div>
                <h2 className="font-heading text-2xl font-semibold text-forest">{cfg.inclusivita.titolo}</h2>
                <p className="mt-2 text-sm leading-relaxed text-forest/80 md:text-base">{cfg.inclusivita.testo}</p>
              </div>
            </div>
          </Card>
        )}

        {/* VANTAGGI */}
        <div className="grid gap-4 sm:grid-cols-2">
          {(cfg?.vantaggi || []).map((v) => {
            const Icon = ICONS[v.tag] || Info;
            return (
              <Card key={v.tag} data-testid={`pf-vantaggio-${v.tag}`} className="rounded-2xl border-border/60 p-5">
                <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-sage text-forest">
                  <Icon className="h-5 w-5" strokeWidth={1.75} />
                </div>
                <h3 className="font-heading text-lg font-semibold text-forest">{v.titolo}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-forest/80">{v.testo}</p>
              </Card>
            );
          })}
        </div>

        {/* VANTAGGI CONCRETI */}
        {(cfg?.vantaggi_concreti || []).length > 0 && (
          <div data-testid="pf-vantaggi-concreti">
            <h2 className="font-heading text-2xl font-semibold text-forest">Perché è diversa dalle altre polizze</h2>
            <p className="mt-1 text-sm text-muted-foreground">Tutele concrete, regole chiare, nessuna sorpresa.</p>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {cfg.vantaggi_concreti.map((v, i) => (
                <div key={i} data-testid={`pf-concreto-${i}`} className="flex items-start gap-3 rounded-2xl border border-border/60 bg-white p-4">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
                  <div>
                    <h3 className="font-heading text-base font-semibold text-forest">{v.titolo}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-forest/80">{v.testo}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CASI STUDIO */}
        {(cfg?.casi_studio || []).length > 0 && (
          <div data-testid="pf-casi-studio">
            <h2 className="font-heading text-2xl font-semibold text-forest">Storie reali, tutela concreta</h2>
            <p className="mt-1 text-sm text-muted-foreground">Come la polizza si adatta alla vita delle persone.</p>
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              {cfg.casi_studio.map((c, i) => (
                <Card key={i} data-testid={`pf-caso-${i}`} className="rounded-2xl border-border/60 p-5">
                  <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-sage px-3 py-1 text-xs font-medium text-forest">
                    <CircleDot className="h-3.5 w-3.5" /> Caso {i + 1}
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-forest">{c.titolo}</h3>
                  <p className="mt-1 text-xs font-medium italic text-muted-foreground">{c.profilo}</p>
                  <p className="mt-2 text-sm leading-relaxed text-forest/80">{c.testo}</p>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* PACCHETTI FORMULA SELF */}
        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-forest px-3 py-1 text-xs font-semibold text-white">
            <ShieldCheck className="h-3.5 w-3.5" /> Formula Self · pacchetti a premio fisso
          </div>
          <h2 className="mt-3 font-heading text-2xl font-semibold text-forest">Pacchetti Formula Self: Tutela Completa · Maxi · Extra</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Tre livelli di protezione a prezzo chiuso, con liquidazione veloce <b>senza scoperti né franchigie</b> per
            le lesioni gravi. Il premio varia in base alla composizione del nucleo familiare.
          </p>
          <div className="mt-5 rounded-2xl border-2 border-forest/20 bg-sage/30 p-4 md:p-5">
            <Label className="font-heading text-base font-semibold text-forest">1 · Scegli la composizione del tuo nucleo</Label>
            <p className="mt-0.5 text-xs text-muted-foreground">I premi dei pacchetti qui sotto si aggiornano automaticamente in base alla tua scelta.</p>
            <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="pf-nucleo">
              {(cfg?.nuclei || []).map((n) => {
                const active = n.code === nucleo;
                return (
                  <button key={n.code} type="button" data-testid={`pf-nucleo-${n.code}`} onClick={() => setNucleo(n.code)}
                    className={`rounded-xl border-2 px-3 py-3 text-center text-sm font-semibold leading-tight transition-all ${active ? "border-forest bg-forest text-white shadow-md" : "border-border/60 bg-white text-forest hover:border-forest/50 hover:bg-white"}`}>
                    {n.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="mt-5 grid gap-4 md:grid-cols-3">
            {(cfg?.packages || []).map((p) => {
              const active = p.code === pacchetto;
              return (
                <Card key={p.code} data-testid={`pf-package-${p.code}`}
                  onClick={() => setPacchetto(p.code)}
                  className={`relative cursor-pointer rounded-2xl border-2 p-5 transition-all ${active ? "border-forest bg-sage/40 shadow-md" : "border-border/60 hover:border-forest/40"}`}>
                  {p.code === "maxi" && (
                    <span className="absolute -top-2.5 right-4 rounded-full bg-terracotta px-2.5 py-0.5 text-[11px] font-semibold text-white">Consigliato</span>
                  )}
                  <h3 className="font-heading text-lg font-semibold text-forest">{p.nome}</h3>
                  <p className="mt-1 min-h-[40px] text-xs text-muted-foreground">{p.descrizione}</p>
                  <div className="mt-3 flex items-baseline gap-1">
                    <span className="font-heading text-3xl font-bold text-forest">{eur(p.premi[nucleo])}</span>
                    <span className="text-xs text-muted-foreground">/anno</span>
                  </div>
                  <ul className="mt-4 space-y-1.5 border-t border-border/50 pt-3">
                    {Object.entries(p.capitali).flatMap(([k, val]) => {
                      const rows = [
                        <li key={k} className="flex items-center justify-between text-xs">
                          <span className="text-forest/70">{gLabel(k)}</span>
                          <span className="font-semibold text-forest">{eur(val)}</span>
                        </li>,
                      ];
                      if (k === "morte" && p.morte_figli != null) {
                        rows.push(
                          <li key={`${k}-figli`} className="flex items-center justify-between text-xs">
                            <span className="pl-2 text-forest/60">↳ Figlio (fino a 26 anni)</span>
                            <span className="font-semibold text-forest">{eur(p.morte_figli)}</span>
                          </li>
                        );
                      }
                      return rows;
                    })}
                  </ul>
                  <Button data-testid={`pf-select-${p.code}`} onClick={(e) => { e.stopPropagation(); scrollToForm(p.code); }}
                    className={`mt-4 w-full gap-1.5 ${active ? "bg-forest text-white hover:bg-forest/90" : "bg-sage text-forest hover:bg-sage/70"}`}>
                    Richiedi questa formula
                  </Button>
                </Card>
              );
            })}
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            Premi annui indicativi, imposte incluse. {cfg?.morte_nota}
          </p>
          <p className="mt-2 text-xs text-muted-foreground">
            Le opzioni «Dopo di Noi» (raddoppio caso morte, maggiorazione mobilità, rimborso ausili/protesi) <b>non
            sono incluse</b> in questi pacchetti: si attivano solo con la Formula Service (vedi sezione dedicata). La
            proposta definitiva sarà confermata dal consulente, secondo le Condizioni di Assicurazione Assimoco.
          </p>
          <div className="mt-4">
            <a href={VOLANTINO_URL} target="_blank" rel="noopener noreferrer" data-testid="pf-volantino-packages"
              className="inline-flex items-center gap-2 rounded-full border border-forest/30 px-4 py-2 text-sm font-medium text-forest transition-colors hover:bg-sage">
              <Download className="h-4 w-4" /> Scarica il volantino da condividere con le famiglie (PDF)
            </a>
          </div>
        </div>

        {/* FOCUS DISABILITÀ SENSORIALE (solo Formula Self) */}
        {cfg?.sensoriale_focus && (
          <div data-testid="pf-sensoriale" className="overflow-hidden rounded-2xl border border-sky-900/15 bg-sky-50 p-6 md:p-8">
            <div className="flex items-start gap-4">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-sky-900 text-white">
                <Eye className="h-5 w-5" strokeWidth={1.75} />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-sky-700">Focus garanzia · Formula Self</p>
                <h2 className="font-heading text-2xl font-semibold text-sky-950">{cfg.sensoriale_focus.titolo}</h2>
                <p className="mt-2 text-sm leading-relaxed text-sky-950/80">{cfg.sensoriale_focus.intro}</p>
              </div>
            </div>
            <div className="mt-5 grid gap-3 sm:grid-cols-3">
              {cfg.sensoriale_focus.voci.map((v, i) => {
                const Icon = SENSO_ICONS[v.icona] || Eye;
                return (
                  <div key={i} data-testid={`pf-senso-voce-${i}`} className="rounded-xl border border-sky-900/10 bg-white p-4">
                    <Icon className="h-6 w-6 text-sky-800" strokeWidth={1.75} />
                    <h3 className="mt-2 font-heading text-base font-semibold text-sky-950">{v.titolo}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-sky-950/75">{v.testo}</p>
                  </div>
                );
              })}
            </div>
            <p className="mt-2 text-[11px] italic text-sky-900/60">{cfg.sensoriale_focus.nota_massimali}</p>
            <div className="mt-4 grid gap-2 sm:grid-cols-3">
              {cfg.sensoriale_focus.vantaggi.map((v, i) => (
                <div key={i} className="flex items-start gap-2">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-sky-700" />
                  <div>
                    <p className="text-sm font-semibold text-sky-950">{v.titolo}</p>
                    <p className="text-xs leading-relaxed text-sky-950/70">{v.testo}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-4 rounded-lg bg-sky-900/5 px-3 py-2 text-[11px] leading-relaxed text-sky-900/70">{cfg.sensoriale_focus.avvertenza}</p>
          </div>
        )}

        {/* FORMULA SERVICE — opzioni Dopo di Noi su misura */}
        {(cfg?.service_opzioni || []).length > 0 && (
          <div data-testid="pf-formula-service">
            <div className="inline-flex items-center gap-2 rounded-full bg-terracotta px-3 py-1 text-xs font-semibold text-white">
              <Sparkles className="h-3.5 w-3.5" /> Formula Service · su misura
            </div>
            <h2 className="mt-3 font-heading text-2xl font-semibold text-forest">Opzione «Dopo di Noi»: protezione su misura</h2>
            <div className="mt-3 flex items-start gap-3 rounded-2xl border-2 border-terracotta/30 bg-terracotta/5 p-4">
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-terracotta" />
              <p className="text-sm leading-relaxed text-forest/90">{cfg.service_intro}</p>
            </div>
            <div className="mt-4 grid gap-4 sm:grid-cols-3">
              {cfg.service_opzioni.map((v) => {
                const Icon = ICONS[v.tag] || Info;
                return (
                  <Card key={v.tag} data-testid={`pf-service-${v.tag}`} className="rounded-2xl border-terracotta/20 p-5">
                    <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-terracotta/10 text-terracotta">
                      <Icon className="h-5 w-5" strokeWidth={1.75} />
                    </div>
                    <h3 className="font-heading text-base font-semibold text-forest">{v.titolo}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-forest/80">{v.testo}</p>
                  </Card>
                );
              })}
            </div>
            <div className="mt-4">
              <Button onClick={() => scrollToForm()} data-testid="pf-service-cta" className="gap-2 bg-terracotta text-white hover:bg-terracotta-hover">
                Richiedi un preventivo Formula Service <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* FORM */}
        <div ref={formRef} data-testid="pf-form-section" className="scroll-mt-20">
          {sent ? (
            <Card data-testid="pf-success" className="rounded-2xl border-forest/20 p-8 text-center">
              <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-600" />
              <h2 className="mt-4 font-heading text-2xl font-semibold text-forest">Richiesta inviata!</h2>
              <p className="mt-2 text-muted-foreground">Grazie. Un nostro consulente ti ricontatterà a breve per la proposta su misura.</p>
            </Card>
          ) : (
            <Card className="rounded-2xl border-forest/20 p-6 md:p-8">
              <form onSubmit={submit} className="space-y-5">
                <div>
                  <h2 className="font-heading text-2xl font-semibold text-forest">Richiedi la tua proposta</h2>
                  <p className="mt-1 text-sm text-muted-foreground">Lascia i tuoi dati: la consulenza è gratuita e senza impegno.</p>
                </div>
                <div className="flex flex-wrap items-center gap-3 rounded-xl bg-sage/50 px-4 py-3 text-sm text-forest">
                  <span className="font-medium">Formula scelta:</span>
                  <span className="rounded-full bg-forest px-3 py-1 text-xs font-semibold text-white">{pkg?.nome}</span>
                  <span className="text-forest/70">{(cfg?.nuclei || []).find((n) => n.code === nucleo)?.label}</span>
                  <span className="ml-auto font-heading text-lg font-bold text-forest" data-testid="pf-premio-stima">{premio != null ? `${eur(premio)}/anno` : "—"}</span>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label>Nome e cognome <span className="text-terracotta">*</span></Label>
                    <Input data-testid="pf-referente" required value={form.referente} onChange={(e) => setForm({ ...form, referente: e.target.value })} className="h-12" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Ente / associazione (facoltativo)</Label>
                    <Input data-testid="pf-ente" value={form.ente} onChange={(e) => setForm({ ...form, ente: e.target.value })} className="h-12" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Email *</Label>
                    <Input data-testid="pf-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-12" />
                    <p className="text-[11px] text-muted-foreground">Riceverai subito via email la documentazione precontrattuale (Set Informativo, DIP, DIP Aggiuntivo e MUP).</p>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Telefono</Label>
                    <Input data-testid="pf-telefono" value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} className="h-12" />
                  </div>
                </div>
                <div className="flex items-center justify-between rounded-xl border border-border/60 px-4 py-3">
                  <div>
                    <Label className="cursor-pointer">Nel nucleo è presente una persona con disabilità grave</Label>
                    <p className="text-xs text-muted-foreground">(art. 3 c. 3 L. 104/92) — per detraibilità 750€ e opzioni «Dopo di Noi» (Formula Service)</p>
                  </div>
                  <Switch data-testid="pf-disabilita" checked={form.disabilita_grave} onCheckedChange={(v) => setForm({ ...form, disabilita_grave: v })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Note (facoltativo)</Label>
                  <Textarea data-testid="pf-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
                </div>
                {error && <p className="text-sm text-red-600" data-testid="pf-error">{error}</p>}
                <PrivacyConsent checked={privacyOk} onCheckedChange={setPrivacyOk} testid="pf-privacy-consent" />
                <Button type="submit" data-testid="pf-submit" disabled={loading || !privacyOk} className="h-12 w-full rounded-xl bg-terracotta text-base hover:bg-terracotta-hover">
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Invia richiesta di proposta"}
                </Button>
              </form>
            </Card>
          )}
        </div>

        {cfg?.compliance && (
          <div data-testid="pf-compliance" className="rounded-xl border border-border/60 bg-muted/40 p-4 text-center">
            <p className="text-xs font-semibold text-forest/90">{cfg.compliance.avvertenza}</p>
            <p className="mt-1.5 text-xs text-muted-foreground">{cfg.compliance.presa_visione}</p>
          </div>
        )}

        <ComplianceNotice />
        <LegalFooterLinks className="pt-2" />
        <p className="pb-6 pt-2 text-center text-xs text-muted-foreground">
          Contenuto informativo, non costituisce proposta contrattuale · verificare sempre le Condizioni di Assicurazione Assimoco e la normativa vigente.
        </p>
      </div>
    </div>
  );
}
